import * as THREE from 'three';
import { LineString, Point } from 'ol/geom';
import { getLength } from 'ol/sphere';
import { WKT } from 'ol/format';
import { Group } from 'three';

import { buildMainMeshByGeomWKT, buildMeshByGeomWKT, getFirstAndLastCoordinatesFromGeomWkt, getOlGeometryFromWKT } from 'features/seismic-3d/components/projections/featureGeomPolygon';
import { lineMetadata } from 'features/seismic-3d/models/types/LineMetadata';
import { GeopostScene } from './GeopostScene';
import { MeshData } from 'features/seismic-3d/models/types/MeshData';
import { Survey3DInfo } from 'features/seismic-3d/models/types/Survey3DInfo';
import { getThreeJsYFactor, getTotalOfTilesFromLine } from 'features/seismic-3d/utils/TileUtils';
import { GeopostTileMesh } from '../mesh/GeopostTileMesh';
import { GeopostThreeJsConstants } from '../utils/GeopostThreeJsConstants';
import { getThreeJsPointsFromOlGeometry, scaleGeometry } from '../utils/ScaleUtils';

export class GeopostGeomBasedScene extends GeopostScene {
    public addGridSection = (linesData: lineMetadata[], volumeToken: string, datum: number, sampleInterval: number) => {
        const grid = this.getObjectByName('grid-' + volumeToken);
        if (grid) {
            linesData.forEach((lineData) => {
                const lineGroup = new THREE.Group();
                lineGroup.name = this.getLineGroupName(lineData.lineNumber, lineData.lineType);
                //const lineMesh = new GeopostLineMesh(lineData.geometryMetadata.TotalTraces, lineData.geometryMetadata.Line, '#d9d9d9', lineData.lineType, lineData.lineNumber);//buildMeshByGeomWKT(lineData.geometryMetadata.Line, null, '#d9d9d9');
                //lineMesh.name = 'line-mesh';
                const lineMesh = buildMeshByGeomWKT(lineData.geometryMetadata.Line, null, '#d9d9d9', this.mainFeatureCentroid);
                lineMesh.position.setY(lineMesh.position.y + getThreeJsYFactor(datum/sampleInterval, sampleInterval));
                const lineMaterial = lineMesh.material;
                if (lineMaterial instanceof THREE.Material) {
                    lineMaterial.transparent = true;
                    lineMesh.name = 'line-mesh';
                    lineGroup.add(lineMesh);
                    grid.add(lineGroup);
                    //lineMesh.renderOrder = -3;
                }
            });
        }
    };

    public addMainMesh = ({geomWKT, centroidWKT, meshColor, meshToken} : MeshData) => {
        const mesh = buildMainMeshByGeomWKT(geomWKT, centroidWKT!, meshColor, this.mainFeatureCentroid);
        mesh.name = meshToken;

        const centroidGeometry = getOlGeometryFromWKT(centroidWKT!);
        const centroid = (centroidGeometry as Point).getCoordinates();
        this.mainFeatureCentroid = [(centroid[0]), (centroid[1])];
        mesh.geometry.computeBoundingBox();
        mesh.matrixAutoUpdate = false;
        mesh.name = 'main-mesh';
        this.mainMesh = mesh;
        (mesh.material as THREE.Material).transparent = true;
        mesh.renderOrder = -3;
        return mesh;
    };

    /**
 * Returns a sub-LineString between two cumulative lengths (in meters).
 * @param line LineString geometry (in EPSG:4326 or EPSG:3857)
 * @param startM Start length along the line in meters
 * @param endM End length along the line in meters
 * @param projection The projection of the line (e.g. 'EPSG:4326')
 * @returns LineString geometry of the subsegment
 */
    private getSubLineStringByLength(
        line: LineString,
        startM: number,
        endM: number,
        projection: string = 'EPSG:4326',
        tileIndex: number,
        totalTiles: number,
    ): LineString {
        if (startM >= endM) { throw new Error('startM must be less than endM') };

        const coords = line.getCoordinates();
        const subCoords: number[][] = [];

        let cumulative = 0;
        let i = 0;

        while (i < coords.length - 1 && cumulative < endM) {
            const [a, b] = [coords[i], coords[i + 1]];
            const segment = new LineString([a, b]);
            const segLength = getLength(segment, { projection });

            const segStart = cumulative;
            const segEnd = cumulative + segLength;

            if (segEnd < startM) {
            // Skip this segment
                cumulative = segEnd;
                i++;
                continue;
            }

            // Compute fractions where to cut
            const segmentFraction = (m: number) =>
                segLength === 0 ? 0 : (m - segStart) / segLength;

            const clamp = (v: number) => Math.max(0, Math.min(1, v));

            const startFrac = clamp(segmentFraction(startM));
            const endFrac = clamp(segmentFraction(endM));

            //if (startFrac === 0 && subCoords.length === 0) { subCoords.push(a) }

            const fracStart = tileIndex/totalTiles;
            const fracEnd = (tileIndex+1)/totalTiles;

            const startInterp = [
                a[0] + (b[0] - a[0]) * startFrac,
                a[1] + (b[1] - a[1]) * startFrac,
            ];
            const endInterp = [
                a[0] + (b[0] - a[0]) * endFrac,
                a[1] + (b[1] - a[1]) * endFrac,
            ];

            if (segStart <= startM && startM < segEnd) { subCoords.push(startInterp) }
            if (segStart < endM && endM <= segEnd) {
                subCoords.push(endInterp);
                break;
            }

            if (segEnd > startM) { subCoords.push(b) }

            cumulative = segEnd;
            i++;
        }

        return new LineString(subCoords);
    }

    /**
 * Creates and adds Three.js plane tiles that follow a polyline path.
 * * This revised function fixes three issues from the original:
 * 1. Uses THREE.CurvePath + THREE.LineCurve3 to *exactly* follow the polyline,
 * instead of a CatmullRomCurve3 which was smoothing it.
 * 2. Calculates mesh segments based on the path's *actual length* for correct resolution.
 * 3. Uses a stable method to generate a vertical ribbon, preventing twisting.
 */
    public addLineTiles = (volumeToken: string, line: lineMetadata, samplesPerTrace: number, sampleInterval: number, datum: number, tileWidth: number, srid: number) => {
    // --- 1. Setup and Geometry Reading (Unchanged) ---
        const { lineNumber, lineType, geometryMetadata: { Line: lineWkt, TotalTraces: numTraces }} = line;
        const olGeom = new WKT().readGeometry(lineWkt, { dataProjection: 'EPSG:' + srid }) as LineString;
        const length = getLength(olGeom as LineString, { projection: 'EPSG:' + srid });

        const totalTiles = Math.ceil(numTraces/tileWidth);
        const metersPerTrace = length / numTraces;
        const metersPerTile = metersPerTrace * tileWidth;
        //const totalTiles = Math.ceil(length/tileLength);
        // Split the main line into sub-lines, one for each tile
        const subLines: LineString[] = [];
        for (let i = 0; i < totalTiles; i += 1) {
            const startM = i * metersPerTile;
            const endM = Math.min(startM + metersPerTile, length);
            if (startM >= endM) { continue }

            subLines.push(this.getSubLineStringByLength(olGeom, startM, endM, 'EPSG:' + srid, i, totalTiles));
        }

        const tileMeshes: THREE.Mesh[] = [];

        // --- 2. Generate Mesh for each Tile (Revised) ---
        subLines.forEach((line, j) => {
            scaleGeometry(line, this.mainFeatureCentroid);
            const points = getThreeJsPointsFromOlGeometry(line);
            if (points.length < 2) {
                debugger; // Safety check
                return;
            }

            // --- FIX 1: Use CurvePath to follow the polyline exactly ---
            // CatmullRomCurve3 creates a *new smooth spline* that doesn't match the original.
            // CurvePath + LineCurve3 creates a path of straight line segments.
            const path = new THREE.CurvePath<THREE.Vector3>();
            for (let i = 0; i < points.length - 1; i++) {
                path.add(new THREE.LineCurve3(points[i], points[i + 1]));
            }
            // -------------------------------------------------------------

            const yFactor = getThreeJsYFactor(samplesPerTrace, sampleInterval);
            const height = yFactor; // Full height of the ribbon

            // --- FIX 2: Correct mesh resolution based on actual path length ---
            const pathLength = path.getLength(); // Get the true geometric length
            // Define resolution (e.g., 1 geometry unit per mesh segment).
            // Adjust this value as needed: smaller = higher resolution.
            const segmentResolution = 0.01;
            const segments = Math.max(1, Math.ceil(pathLength / segmentResolution));
            // -----------------------------------------------------------------

            const uvs: number[] = [];
            const vertices: number[] = [];
            const indices: number[] = [];

            const geometry = new THREE.BufferGeometry();

            const sampleOffset = getThreeJsYFactor(datum / sampleInterval, sampleInterval);

            // Build the ribbon segment by segment
            for (let i = 0; i <= segments; i++) {
                const u = i / segments; // U-coordinate and path parameter
                const current = path.getPoint(u);

                // --- FIX 3: Stable vertex generation for a vertical ribbon ---
                // This creates a "curtain" that hangs vertically *down* (negative Y)
                // from the path. It's stable and won't twist.

                // TOP of ribbon is at the path
                const top = new THREE.Vector3().copy(current);
                //top.setY(top.y + sampleOffset);

                // BOTTOM is 'height' units directly below the top (assuming Y is the vertical axis)
                const bottom = new THREE.Vector3(current.x, current.y - height, current.z);
                // --------------------------------------------------------------

                vertices.push(top.x, top.y, top.z);
                vertices.push(bottom.x, bottom.y, bottom.z);

                uvs.push(u, 0); // top vertex
                uvs.push(u, 1); // bottom vertex

                // Add indices for the two triangles that form this quad
                if (i > 0) {
                    const base = i * 2;
                    indices.push(
                        base - 2, base - 1, base + 0, // Triangle 1 (TopLeft, BottomLeft, TopRight)
                        base - 1, base + 1, base + 0  // Triangle 2 (BottomLeft, BottomRight, TopRight)
                    );
                }
            }

            // --- 3. Assemble Mesh (Mostly Unchanged) ---
            geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
            geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
            geometry.setIndex(indices);
            geometry.computeVertexNormals(); // Good for lighting, though not used by MeshBasicMaterial

            const material = new THREE.MeshBasicMaterial({
                color: '#cccccc',
                side: THREE.DoubleSide,
                transparent: true,
                opacity: 0.4
            });

            const tileMesh = new THREE.Mesh(geometry, material);
            tileMesh.name = this.getTileObjectName(j, lineNumber, lineType, 0);
            tileMeshes.push(tileMesh);
        });

        // --- 4. Add to Scene (Unchanged) ---
        const lineGroup = this.getLineGroup(volumeToken, lineNumber, lineType);
        const tileGroup = new THREE.Group();
        tileGroup.name = this.getTileGroupName(lineNumber, lineType);
        tileGroup.visible = false;
        tileGroup.userData.totalTiles = totalTiles;

        tileGroup.add(...tileMeshes);
        // ... (rest of your commented-out code) ...

        if (lineGroup) {
            lineGroup.add(tileGroup);
        }
    };
    public addZSliceTilesByDepth = (volumeToken: string, totalInlines: number, totalXlines: number, inlineStart: number, xlineStart: number, depth: number, sampleInterval: number, tileWidth: number, tileHeight: number) => {};

    public addLineTiles2 = (volumeToken: string, line: lineMetadata, samplesPerTrace: number, sampleInterval: number, datum: number, tileWidth: number, srid: number) => {
        const { lineNumber, lineType, geometryMetadata: { Line: lineWkt, TotalTraces: totalTraces }} = line;
        const [ firstLineCoordinate, lastLineCoordinate ] = getFirstAndLastCoordinatesFromGeomWkt(line.geometryMetadata.Line, this.mainFeatureCentroid);
        const totalTiles = getTotalOfTilesFromLine(line.geometryMetadata.TotalTraces, tileWidth);
        const yFactor = getThreeJsYFactor(samplesPerTrace, sampleInterval);

        const lineGroup = this.getLineGroup(volumeToken, lineNumber, lineType);
        const tileGroup = new Group();
        tileGroup.name = this.getTileGroupName(lineNumber, lineType);
        tileGroup.visible = false;
        //AQUI CALCULA
        for (let tileIndex = 0; tileIndex < totalTiles; tileIndex++) {
            const tileObject = new GeopostTileMesh(firstLineCoordinate, lastLineCoordinate, totalTiles, tileIndex, yFactor, 0);
            tileObject.name = this.getTileObjectName(tileIndex, lineNumber, lineType, 0);
            tileObject.geometry.computeBoundingBox();
            tileObject.userData.isFilled = false;
            //lineType === LineType.Inline ? inlineTilesRenderOrder : xlineTilesRenderOrder;

            tileGroup.add(tileObject);
        }
        if (lineGroup) {
            lineGroup.add(tileGroup);
        }
    };

}